import { Suspense, lazy, type ComponentType } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { AdmissionNotice } from '@cdn/admission';
import { OrganizationScope, SessionGate, SignIn, SignInReturn } from '@cdn/identity';
import { Paths } from '@cdn/navigation';
import { Frame, PublicFrame } from '@cdn/shell';
import { Announcement, NotFound, Waiting } from '@cdn/ui';

/**
 * A screen of a section module loaded when its route is first opened, so the first page carries only the
 * shell; the screens, the applications module included, come with their first route. The module's public entry
 * is what is loaded, as for any import.
 */
function screen<Module>(load: () => Promise<Module>, name: keyof Module) {
	const Lazy = lazy(async () => ({ default: (await load())[name] as unknown as ComponentType }));
	return function Screen() {
		return (
			<Suspense fallback={<Waiting />}>
				<Lazy />
			</Suspense>
		);
	};
}

const Home = screen(() => import('./home'), 'Home');
const ApplicationList = screen(() => import('@cdn/applications'), 'ApplicationList');
const ApplicationLayout = screen(() => import('@cdn/applications'), 'ApplicationLayout');
const ApplicationOverview = screen(() => import('@cdn/applications'), 'ApplicationOverview');
const ApplicationSettings = screen(() => import('@cdn/applications'), 'ApplicationSettings');
const DefineTarget = screen(() => import('@cdn/applications'), 'DefineTarget');
const AddDomain = screen(() => import('@cdn/domains'), 'AddDomain');
const InviteGuest = screen(() => import('@cdn/access'), 'InviteGuest');
const ProjectsScreen = screen(() => import('@cdn/applications'), 'ProjectsScreen');
const AccessScreen = screen(() => import('@cdn/access'), 'AccessScreen');
const ConsumptionScreen = screen(() => import('@cdn/consumption'), 'ConsumptionScreen');
const PlanScreen = screen(() => import('@cdn/consumption'), 'PlanScreen');
const WaitlistEntry = screen(() => import('@cdn/consumption'), 'WaitlistEntry');
const AccessEntry = screen(() => import('@cdn/admission'), 'AccessEntry');
const DomainsScreen = screen(() => import('@cdn/domains'), 'DomainsScreen');
const InboxScreen = screen(() => import('@cdn/notifications'), 'InboxScreen');
const ProvidersScreen = screen(() => import('@cdn/providers'), 'ProvidersScreen');
const ProviderTask = screen(() => import('@cdn/providers'), 'ProviderTask');
const RegisterApplication = screen(() => import('@cdn/applications'), 'RegisterApplication');
const AdoptApplication = screen(() => import('@cdn/applications'), 'AdoptApplication');
const RegisterDependencies = screen(() => import('@cdn/registrations'), 'RegisterDependencies');
const RegistrationsScreen = screen(() => import('@cdn/registrations'), 'RegistrationsScreen');
const RegistrationDetail = screen(() => import('@cdn/registrations'), 'RegistrationDetail');
const PublishCapture = screen(() => import('@cdn/registrations'), 'PublishCapture');
const ReleasesScreen = screen(() => import('@cdn/releases'), 'ReleasesScreen');
const ReleaseDetail = screen(() => import('@cdn/releases'), 'ReleaseDetail');
const JobsScreen = screen(() => import('@cdn/jobs'), 'JobsScreen');
const JobDetail = screen(() => import('@cdn/jobs'), 'JobDetail');

/**
 * Every signed-in page, in the frame; a person not yet admitted reads the admission notice right after the
 * page's header
 */
function Console() {
	return (
		<SessionGate>
			<Frame>
				<Announcement.Provider value={<AdmissionNotice />}>
					<Outlet />
				</Announcement.Provider>
			</Frame>
		</SessionGate>
	);
}

/** The organization's home opens its applications, keeping what the address carries (`?from=` among it) */
function Forward() {
	const { search } = useLocation();
	return <Navigate to={{ pathname: 'applications', search }} replace />;
}

function Scope() {
	return (
		<OrganizationScope>
			<Outlet />
		</OrganizationScope>
	);
}

/**
 * The route table. Every management view lives under its organization; `/` and `/waitlist` are the two
 * addresses Beyond Landing links to, and both survive signing in through the return destination.
 */
export function Routing() {
	const { patterns } = Paths;
	return (
		<Routes>
			<Route path={patterns.signin} element={<PublicFrame><SignIn /></PublicFrame>} />
			<Route path={patterns.returning} element={<PublicFrame><SignInReturn /></PublicFrame>} />
			<Route element={<Console />}>
				<Route path={patterns.home} element={<Home />} />
				<Route path={patterns.waitlist} element={<WaitlistEntry />} />
				<Route path={patterns.access} element={<AccessEntry />} />
				<Route path={patterns.notifications} element={<InboxScreen />} />
				<Route path={patterns.organization} element={<Scope />}>
					<Route index element={<Forward />} />
					<Route path="applications" element={<ApplicationList />} />
					<Route path="applications/new" element={<RegisterApplication />} />
					<Route path="applications/adopt" element={<AdoptApplication />} />
					<Route path="publish" element={<PublishCapture />} />
					<Route path="applications/:application" element={<ApplicationLayout />}>
						<Route index element={<ApplicationOverview />} />
						<Route path="settings" element={<ApplicationSettings />} />
						<Route path="targets/new" element={<DefineTarget />} />
						<Route path="registrations" element={<RegistrationsScreen />} />
						<Route path="registrations/new" element={<RegisterDependencies />} />
						<Route path="registrations/:registration" element={<RegistrationDetail />} />
						<Route path="jobs" element={<JobsScreen />} />
						<Route path="jobs/:job" element={<JobDetail />} />
						<Route path="releases" element={<ReleasesScreen />} />
						<Route path="releases/:release" element={<ReleaseDetail />} />
						<Route path="domains" element={<DomainsScreen />} />
						<Route path="domains/new" element={<AddDomain />} />
						<Route path="access" element={<AccessScreen />} />
						<Route path="access/invite" element={<InviteGuest />} />
					</Route>
					<Route path="projects" element={<ProjectsScreen />} />
					<Route path="consumption" element={<ConsumptionScreen />} />
					<Route path="plan" element={<PlanScreen />} />
					<Route path="providers" element={<ProvidersScreen />} />
					<Route path="providers/:provider" element={<ProviderTask />} />
				</Route>
				<Route path="*" element={<NotFound />} />
			</Route>
		</Routes>
	);
}
